import { useRef, useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * TagInput — chip-style multi-value text input.
 *
 * Props:
 *   value:       string[]          — controlled list of tags
 *   onChange:    (tags: string[]) => void
 *   placeholder: string            — shown when empty and no input
 *   disabled:    boolean
 *   maxTags:     number            — optional upper limit (default unlimited)
 *   className:   string            — extra classes on the outer wrapper
 *
 * UX:
 *   • Type a value + press Enter or comma to add a tag.
 *   • Press Backspace on empty input to remove the last tag.
 *   • Click the × on any chip to remove it.
 *   • Duplicate tags are silently ignored.
 *   • Tags are trimmed before adding.
 */
export function TagInput({
    value = [],
    onChange,
    placeholder = 'Type and press Enter…',
    disabled = false,
    maxTags,
    className,
}) {
    const [input, setInput] = useState('');
    const inputRef = useRef(null);

    const addTag = (raw) => {
        const tag = raw.trim();
        if (!tag) return;
        if (value.includes(tag)) { setInput(''); return; }          // no duplicates
        if (maxTags && value.length >= maxTags) { setInput(''); return; }
        onChange([...value, tag]);
        setInput('');
    };

    const removeTag = (idx) => {
        onChange(value.filter((_, i) => i !== idx));
    };

    const handleKeyDown = (e) => {
        if ((e.key === 'Enter' || e.key === ',') && input.trim()) {
            e.preventDefault();
            addTag(input.replace(/,$/, '')); // strip trailing comma if typed
        }
        if (e.key === 'Backspace' && !input && value.length > 0) {
            removeTag(value.length - 1);
        }
    };

    const handleBlur = () => {
        if (input.trim()) addTag(input);
    };

    return (
        <div
            className={cn(
                'flex min-h-[2.5rem] flex-wrap gap-1.5 rounded-md border border-input',
                'bg-background px-3 py-2 text-sm ring-offset-background',
                'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
                disabled && 'cursor-not-allowed opacity-50',
                className,
            )}
            onClick={() => !disabled && inputRef.current?.focus()}
        >
            {/* Existing chips */}
            {value.map((tag, i) => (
                <span
                    key={i}
                    className="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-0.5 text-xs font-medium text-foreground"
                >
                    {tag}
                    {!disabled && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); removeTag(i); }}
                            className="ml-0.5 rounded-sm text-muted-foreground hover:text-foreground focus:outline-none"
                            aria-label={`Remove ${tag}`}
                        >
                            <X className="h-3 w-3" />
                        </button>
                    )}
                </span>
            ))}

            {/* Text input */}
            {(!maxTags || value.length < maxTags) && (
                <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    disabled={disabled}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    placeholder={value.length === 0 ? placeholder : ''}
                    className="flex-1 min-w-[8rem] bg-transparent outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
                />
            )}
        </div>
    );
}

export default TagInput;
